import { useTranslation } from "react-i18next";
import { TriangleAlert } from "lucide-react";
import { Dialog, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Disclosure } from "@/components/ui/disclosure";
import { Button } from "@/components/ui/button";
import { FacilityIcon } from "@/components/icons";
import type { ResolvedError } from "./resolveError.js";

/**
 * The single, app-wide error dialog (mounted once by ErrorProvider). Shows a title + message and — when the error
 * carries structured detail — an expandable list of the offending entities (e.g. the facilities holding a category
 * you tried to delete), turning a dead-end into an actionable to-do. Modal a11y (focus-trap, Esc, focus-restore)
 * comes from the Dialog primitive. Conditionally mounted, so closing simply unmounts it.
 */
export function ErrorDialog({ error, onClose }: { error: ResolvedError | null; onClose: () => void }) {
  const { t } = useTranslation();
  if (!error) return null;
  return (
    <Dialog isOpen onOpenChange={(open) => !open && onClose()}>
      <div className="flex items-start gap-3">
        <span className="bg-destructive/10 text-destructive flex size-9 shrink-0 items-center justify-center rounded-full">
          <TriangleAlert aria-hidden="true" className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <DialogTitle>{error.title}</DialogTitle>
          <DialogDescription className="mt-1">{error.message}</DialogDescription>
        </div>
      </div>

      {error.details ? (
        <Disclosure title={error.details.label} className="mt-1">
          <ul className="divide-border/60 max-h-56 divide-y overflow-y-auto">
            {error.details.items.map((it) => (
              <li key={it.id} className="flex items-center gap-2 px-3 py-2">
                {/* details.items are facilities today (CATEGORY_HAS_ATTACHMENTS) — the universal facility icon. */}
                <FacilityIcon aria-hidden="true" className="text-muted-foreground/70 size-4 shrink-0" />
                <span className="text-foreground truncate text-sm font-medium">{it.name}</span>
              </li>
            ))}
          </ul>
        </Disclosure>
      ) : null}

      <DialogFooter>
        <Button onPress={onClose}>{t("errors.close")}</Button>
      </DialogFooter>
    </Dialog>
  );
}
